/**
 * Title Cell Renderer
 *
 * Renders the vulnerability title/name with optional description (DNS).
 * Used in the vulnerability name column of the vulnerabilities table.
 */

import React from 'react';

import { NameColumn } from '@/components/NameColumn';
import type { RiskWithVulnerability } from '@/hooks/useRisks';

interface TitleCellProps {
  risk: RiskWithVulnerability;
}

/**
 * Memoized cell renderer for vulnerability title column to improve performance
 *
 * Displays the vulnerability title (or falls back to name) with DNS as description.
 * For group headers, displays a count badge next to the title.
 * Clicking the cell opens the vulnerability drawer for detailed information.
 *
 * @param risk - The vulnerability/risk to render (or group header)
 * @returns NameColumn component with title and description (or title with count badge for groups)
 */
export const TitleCell: React.FC<TitleCellProps> = React.memo(({ risk }) => {
  // Check if this is a group header
  const isGroupHeader = !!(risk as unknown as Record<string, unknown>)
    ._isGroupHeader;
  const childCount = (risk as unknown as Record<string, unknown>)
    ._childCount as number | undefined;

  // Prefer title over name for display
  const displayName = risk?.title || risk.name;
  const description = risk.dns;

  // For group headers, render with count badge
  if (isGroupHeader && childCount) {
    return (
      <div className="flex items-center gap-2">
        <span>{displayName}</span>
        <span className="bg-chariot-layer2 text-chariot-secondary inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular-nums">
          {childCount}
        </span>
      </div>
    );
  }

  // For regular rows, use NameColumn component
  return <NameColumn name={displayName} description={description} />;
});

TitleCell.displayName = 'TitleCell';
